import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';
import PackagePreviewCard from '../../components/common/PackagePreviewCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Register: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useUserAuth();
  
  // Get package info from navigation state
  const selectedPackage = location.state as { packageId?: string; country?: string } || {};
  
  // Package details state
  const [packageDetails, setPackageDetails] = useState<any>(null);
  const [packageLoading, setPackageLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    whatsapp: '',
    password: '',
    confirmPassword: ''
  });
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    whatsapp: false,
    password: false,
    confirmPassword: false
  });
  const [passwordChecklist, setPasswordChecklist] = useState({
    minLength: false,
    hasLetter: false,
    hasNumber: false
  });

  // Validation functions
  const validateField = (name: string, value: string) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = t('validation.nameRequired');
        } else if (value.trim().length < 2) {
          error = t('validation.nameMinLength');
        }
        break;

      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) {
          error = t('validation.emailRequired');
        } else if (!emailRegex.test(value)) {
          error = t('validation.emailInvalid');
        }
        break;

      case 'whatsapp':
        // Strict international format: +followed by digits only, 10-15 digits total
        const phoneRegex = /^\+\d{10,15}$/;
        if (!value) {
          error = t('validation.whatsappRequired');
        } else if (!phoneRegex.test(value)) {
          error = t('validation.whatsappInvalid');
        }
        break;

      case 'password':
        // Update password checklist - back to 6 characters minimum
        const minLength = value.length >= 6;
        const hasLetter = /[a-zA-Z]/.test(value);
        const hasNumber = /\d/.test(value);

        setPasswordChecklist({
          minLength,
          hasLetter,
          hasNumber
        });

        // Only show error if field is touched and has value but doesn't meet requirements
        if (!value) {
          error = t('validation.passwordRequired');
        } else if (!minLength || !hasLetter || !hasNumber) {
          // Don't set error message - let the checklist show the requirements
          error = '';
        }
        break;

      case 'confirmPassword':
        if (!value) {
          error = t('validation.confirmPasswordRequired');
        } else if (value !== formData.password) {
          error = t('validation.passwordsNoMatch');
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors: any = {};
    Object.keys(formData).forEach(key => {
      newErrors[key] = validateField(key, (formData as any)[key]);
    });

    // Special check for password - ensure all checklist items are satisfied
    if (formData.password && (!passwordChecklist.minLength || !passwordChecklist.hasLetter || !passwordChecklist.hasNumber)) {
      newErrors.password = t('validation.passwordRequirements');
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch package details when component mounts
  useEffect(() => {
    if (selectedPackage.packageId) {
      fetchPackageDetails(selectedPackage.packageId);
    }
  }, [selectedPackage.packageId]);

  const fetchPackageDetails = async (packageId: string) => {
    setPackageLoading(true);
    try {
      const { default: apiService } = await import('../../services/api');
      const data = await apiService.getPackage(packageId);
      
      if (data.success && data.package) {
        setPackageDetails({
          id: data.package.id,
          name: data.package.name,
          description: data.package.description,
          price: parseFloat(data.package.price),
          currency: data.package.currency,
          features: Array.isArray(data.package.features) ? data.package.features : JSON.parse(data.package.features || '[]'),
          processingTime: data.package.processing_time,
          popular: data.package.popular,
          country: {
            name: data.package.country_name,
            code: data.package.country_code,
            flag: data.package.country_flag || '🌍'
          }
        });
      }
    } catch (error) {    } finally {
      setPackageLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched to show validation errors
    setTouched({
      name: true,
      email: true,
      whatsapp: true,
      password: true,
      confirmPassword: true
    });

    // Validate the entire form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await register({
        name: formData.name,
        email: formData.email,
        whatsapp: formData.whatsapp,
        password: formData.password,
        language: i18n.language
      });

      // Check if email verification is required
      if (response.requires_verification) {
        navigate('/verify-email', {
          state: {
            email: formData.email,
            selectedPackage: packageDetails
          }
        });
      } else {
        // Redirect to requests page (not payment) with package details
        if (packageDetails) {
          navigate('/dashboard/requests', {
            state: {
              selectedPackage: packageDetails,
              message: 'Account created successfully! Your selected package is ready to complete.'
            }
          });
        } else {
          // New users without preselected package should go to requests page to start creating their first request
          navigate('/dashboard/requests', {
            state: {
              message: 'Account created successfully! Ready to create your first company formation request.'
            }
          });
        }
      }
    } catch (error: any) {
      setLoading(false);
      setErrors(prev => ({
        ...prev,
        email: error.message || 'Registration failed'
      }));    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value
    });

    // Always validate password in real-time, other fields only if touched
    if (name === 'password' || touched[name as keyof typeof touched]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));

      // Special case: if password changes, also validate confirmPassword
      if (name === 'password' && touched.confirmPassword) {
        const confirmError = validateField('confirmPassword', formData.confirmPassword);
        setErrors(prev => ({
          ...prev,
          confirmPassword: confirmError
        }));
      }
    } else if (name === 'password') {
      // Always update password checklist even if field not touched yet
      validateField(name, value);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field on blur
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          {t('auth.register')}
        </h2>
        {selectedPackage.packageId && (
          <div className="mt-4">
            {packageLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
                <span className="ml-2 text-sm text-gray-600">Loading package details...</span>
              </div>
            ) : packageDetails ? (
              <PackagePreviewCard 
                package={packageDetails} 
                className="max-w-md mx-auto"
              />
            ) : (
              <p className="text-center text-sm text-red-600 bg-red-50 p-2 rounded">
                Unable to load package details
              </p>
            )}
          </div>
        )}
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
            {t('auth.login')}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.name')}
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.email')}
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.whatsapp')}
              </label>
              <div className="mt-1">
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="+1234567890"
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                    errors.whatsapp ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.whatsapp && (
                  <p className="mt-1 text-sm text-red-600">{errors.whatsapp}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    {t('validation.passwordRequirements')}:
                  </div>
                  <div className="space-y-1">
                    <div className={`flex items-center text-xs ${
                      passwordChecklist.minLength ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {passwordChecklist.minLength ? (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                      {t('validation.minLength6')}
                    </div>
                    <div className={`flex items-center text-xs ${
                      passwordChecklist.hasLetter ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {passwordChecklist.hasLetter ? (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                      {t('validation.containsLetter')}
                    </div>
                    <div className={`flex items-center text-xs ${
                      passwordChecklist.hasNumber ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {passwordChecklist.hasNumber ? (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                      {t('validation.containsNumber')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.confirmPassword')}
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {loading ? 'Creating Account...' : t('auth.registerBtn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;